import { Outlet } from '@tanstack/react-router';
import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useMe } from '@/api/queries/me';
import { AppShell } from '@/components/shell/AppShell';
import { QueryBoundary } from '@/components/state/QueryBoundary';
import { useToast } from '@/components/ui/Toast';
import { useRememberLastEdition } from '@/features/festivals/last-edition';
import { requestBellOpen } from '@/features/notifications/bell-signal';
import { GuestBar } from '@/features/guest/GuestBar';
import { SupportBar } from '@/features/control-room/support/SupportBar';
import { useRealtime } from '@/features/realtime/use-realtime';

/**
 * The authenticated subtree. `/v1/me` is loaded once in `beforeLoad` and read from cache here;
 * the boundary exists for the refetch case, not the first paint.
 */
export function AuthenticatedLayout(): ReactNode {
  return (
    <QueryBoundary>
      <Outlet />
    </QueryBoundary>
  );
}

export function ShellLayout(): ReactNode {
  const { t } = useTranslation();
  const me = useMe();
  const { toast } = useToast();

  /*
   * TOAST NÅR NOEN NEVNER DEG (KOI Del 4: «toast for @-nevning når du er på en annen side»).
   *
   * ==============================================================================================
   * BARE NEVNINGER, og det er ordrens egen avgrensning. Et varsel om at en oppgave forfaller er
   * noe man ser i klokka når man ser etter; en nevning er noen som snakker til deg, og den skal
   * nå fram uten at man tilfeldigvis åpner en meny.
   *
   * SETNINGEN ER GENERELL, IKKE VARSELETS EGEN. Strømmen bærer id-er og ingenting annet (0239).
   * For en melding slår `useRealtime` opp varselet for å vite OM det er en nevning (QA6 K6, M7),
   * men toasten sier bare at du er nevnt, og KNAPPEN åpner bjella, der setningen står (276.10: bjella er
   * det eneste stedet man får varsler).
   *
   * KLOKKA ER ALT OPPDATERT når toasten vises: \`useRealtime\` invaliderer varselnøklene før den
   * kaller hit, så merket i toppen og lista bak knappen er ferske i det man trykker.
   */
  const onMention = useCallback(() => {
    toast({
      title: t('notifications.toast.mentioned'),
      action: {
        label: t('notifications.toast.open'),
        onAction: () => {
          requestBellOpen();
        },
      },
    });
  }, [toast, t]);

  /*
   * SANNTIDSSTRØMMEN ÅPNES HER, ØVERST, OG BARE HER.
   *
   * `ShellLayout` er den ene komponenten som står montert så lenge man er logget inn i et hus, og
   * som monteres på nytt når man bytter hus. Det er nøyaktig strømmens levetid.
   *
   * To komponenter som hver åpnet sin ville gitt to forbindelser per fane og to invalideringer per
   * hendelse — og den andre ville kommet i det React rakk å tegne ferdig.
   */
  /*
   * GJESTEMODUS (sak 232, T5): strømmen er AV. Gjestens seter og varsler ligger i hens egne hus, så
   * en strøm i organisasjonen som delte ville aldri båret noe til hen — og tråden leses i Innboks.
   */
  const guest = me.activeOrgRole === 'guest';
  useRealtime(guest ? null : me.activeOrganizationId, { onMention });

  /* Sak 217.13: årgangen man står i huskes her, én gang for hele skallet. */
  useRememberLastEdition(me.activeOrganizationId);

  return (
    /* A11 (1031): det oransje båndet når man er inne som support. */
    <AppShell me={me} banner={guest ? <GuestBar me={me} /> : <SupportBar me={me} />}>
      <Outlet />
    </AppShell>
  );
}
